export interface TimePeriodOption {
  label: string;
  value: string;
  getDays: () => number; // Function to calculate days dynamically
}

export const TIME_PERIOD_OPTIONS: TimePeriodOption[] = [
  { 
    label: "Today", 
    value: "today", 
    getDays: () => 1 
  },
  { 
    label: "Last 3 Days", 
    value: "3days", 
    getDays: () => 3 
  },
  { 
    label: "Last 7 Days", 
    value: "7days", 
    getDays: () => 7 
  },
  { 
    label: "Last 14 Days", 
    value: "14days", 
    getDays: () => 14 
  },
  { 
    label: "Last Month", 
    value: "1month", 
    getDays: () => {
      const now = new Date();
      // Get the number of days in the previous month
      const daysInPreviousMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
      return daysInPreviousMonth;
    }
  },
  { 
    label: "Last 3 Months", 
    value: "3months", 
    getDays: () => {
      const now = new Date();
      let totalDays = 0;
      
      // Count days in the last 3 completed months
      for (let i = 1; i <= 3; i++) {
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 0).getDate();
        totalDays += daysInMonth;
      }
      
      return totalDays;
    }
  },
  { 
    label: "Last 6 Months", 
    value: "6months", 
    getDays: () => {
      const now = new Date();
      let totalDays = 0;
      
      // Count days in the last 6 completed months
      for (let i = 1; i <= 6; i++) {
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 0).getDate();
        totalDays += daysInMonth;
      }
      
      return totalDays;
    }
  },
  { 
    label: "Last 1 Year", 
    value: "1year", 
    getDays: () => {
      const now = new Date();
      const currentYear = now.getFullYear();
      const isCurrentLeapYear = isLeapYear(currentYear);
      const isPreviousLeapYear = isLeapYear(currentYear - 1);
      
      // If we're past February in current year, check current year
      // Otherwise, check if previous year was leap year
      if (now.getMonth() >= 2) {
        return isCurrentLeapYear ? 366 : 365;
      } else {
        return isPreviousLeapYear ? 366 : 365;
      }
    }
  },
];

/**
 * Converts a time period value to number of days
 * Handles variable month lengths and leap years accurately
 * @param periodValue - The time period value (e.g., "today", "7days", "1month")
 * @returns The exact number of days for the given period
 */
export function convertPeriodToDays(periodValue: string): number {
  const option = TIME_PERIOD_OPTIONS.find(opt => opt.value === periodValue);
  return option ? option.getDays() : 1; // Default to 1 day if not found
}

/**
 * Gets the time period option by value
 * @param periodValue - The time period value
 * @returns The TimePeriodOption object or undefined
 */
export function getTimePeriodOption(periodValue: string): TimePeriodOption | undefined {
  return TIME_PERIOD_OPTIONS.find(opt => opt.value === periodValue);
}

/**
 * Gets the label for a time period value
 * @param periodValue - The time period value
 * @returns The human-readable label
 */
export function getTimePeriodLabel(periodValue: string): string {
  const option = getTimePeriodOption(periodValue);
  return option ? option.label : "Today";
}

/**
 * Gets the exact number of days for a time period with debug info
 * Useful for debugging and understanding calculations
 * @param periodValue - The time period value
 * @returns Object with days count and calculation details
 */
export function getTimePeriodDetails(periodValue: string): { 
  days: number; 
  startDate: Date; 
  endDate: Date; 
  calculation: string;
} {
  const now = new Date();
  const option = getTimePeriodOption(periodValue);
  
  if (!option) {
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return {
      days: 1,
      startDate: oneDayAgo,
      endDate: now,
      calculation: "Default: 1 day"
    };
  }

  const days = option.getDays();
  const startDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
  
  let calculation = "";
  switch (periodValue) {
    case "1month":
      calculation = `1 month ago to now (accounting for ${new Date(now.getFullYear(), now.getMonth() - 1, 0).getDate()} days in previous month)`;
      break;
    case "3months":
      calculation = "3 months ago to now (accounting for actual calendar months)";
      break;
    case "6months":
      calculation = "6 months ago to now (accounting for actual calendar months)";
      break;
    case "1year":
      calculation = `1 year ago to now (accounting for ${isLeapYear(now.getFullYear()) ? 'leap' : 'regular'} year)`;
      break;
    default:
      calculation = `Fixed ${days} days`;
  }

  return { days, startDate, endDate: now, calculation };
}

/**
 * Helper function to check if a year is a leap year
 * @param year - The year to check
 * @returns True if the year is a leap year
 */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}